import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaBalanceScale,
  FaCamera,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaSync,
  FaTimes,
} from 'react-icons/fa';
import { disputeAdminService, resolveProductMediaUrl } from '../../../../services/apiService';

const FAULT_OPTIONS = [
  { value: 'seller_total', label: 'Seller fault — total defect' },
  { value: 'seller_partial', label: 'Seller fault — partial defect' },
  { value: 'carrier', label: 'Carrier fault — transit damage' },
  { value: 'catalog_fitment', label: 'Catalog fitment error' },
  { value: 'buyer_fraud', label: 'Buyer fraud' },
  { value: 'rejected', label: 'Claim rejected (no photo / invalid)' },
];

const COMPARISON_STYLES = {
  pass: 'bg-emerald-100 text-emerald-800',
  moderate: 'bg-amber-100 text-amber-800',
  high_divergence: 'bg-red-100 text-red-800',
};

function formatDt(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SlaBadge({ label, deadline, overdue, urgent }) {
  const tone = overdue
    ? 'border-red-300 bg-red-50 text-red-800'
    : urgent
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-slate-200 bg-white text-slate-700';
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${tone}`}>
      <p className="font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 font-medium">{formatDt(deadline)}</p>
      {overdue && (
        <p className="mt-1 flex items-center gap-1 text-red-700">
          <FaExclamationTriangle /> Overdue
        </p>
      )}
    </div>
  );
}

function PhotoTile({ title, url, comparisonResult, comparisonScore, tone }) {
  const resolved = url ? resolveProductMediaUrl(url) : '';
  const borderTone =
    tone === 'listing'
      ? 'border-emerald-300'
      : tone === 'dispatch'
      ? 'border-orange-300'
      : 'border-pink-300';

  return (
    <div className={`rounded-xl border-2 ${borderTone} bg-white overflow-hidden`}>
      <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase text-slate-600">{title}</span>
        {comparisonResult && (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              COMPARISON_STYLES[comparisonResult] || 'bg-slate-100 text-slate-600'
            }`}
          >
            {comparisonResult.replace(/_/g, ' ')}
            {comparisonScore != null ? ` (${comparisonScore}%)` : ''}
          </span>
        )}
      </div>
      <div className="aspect-square bg-slate-100 flex items-center justify-center">
        {resolved ? (
          <img src={resolved} alt={title} className="h-full w-full object-cover" />
        ) : (
          <div className="text-center text-slate-400 p-4">
            <FaCamera className="mx-auto mb-2 text-2xl" />
            <p className="text-xs">Not uploaded</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DisputeArbitrationPanel() {
  const [bucket, setBucket] = useState('active');
  const [view, setView] = useState('disputes');
  const [disputes, setDisputes] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [faultType, setFaultType] = useState('');
  const [refundPct, setRefundPct] = useState(50);
  const [adminNotes, setAdminNotes] = useState('');
  const [evidenceParty, setEvidenceParty] = useState('buyer');
  const [evidenceNote, setEvidenceNote] = useState('');

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      if (view === 'appeals') {
        const rows = await disputeAdminService.listAppeals('open');
        setAppeals(rows);
        setDisputes([]);
      } else {
        const rows = await disputeAdminService.listDisputes(bucket);
        setDisputes(rows);
        setAppeals([]);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load queue');
      setDisputes([]);
      setAppeals([]);
    } finally {
      setLoading(false);
    }
  }, [bucket, view]);

  const loadDetail = useCallback(async (claimId) => {
    if (!claimId) return;
    try {
      setDetailLoading(true);
      const row = await disputeAdminService.getDispute(claimId);
      setDetail(row);
      setFaultType(row.faultType || row.suggestedFaultType || '');
      setAdminNotes(row.adminNotes || '');
      if (row.buyerRefundPct) setRefundPct(row.buyerRefundPct);
    } catch (err) {
      toast.error(err.message || 'Failed to load dispute detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedClaimId) loadDetail(selectedClaimId);
  }, [selectedClaimId, loadDetail]);

  const refreshAll = async () => {
    await loadList();
    if (selectedClaimId) await loadDetail(selectedClaimId);
  };

  const handleSelect = (claimId) => {
    setSelectedClaimId(claimId);
  };

  const handleRequestEvidence = async () => {
    if (!selectedClaimId) return;
    if ((evidenceNote || '').trim().length < 10) {
      toast.warn('Evidence request note must be at least 10 characters');
      return;
    }
    try {
      setActing(true);
      await disputeAdminService.requestEvidence(selectedClaimId, {
        party: evidenceParty,
        description: evidenceNote.trim(),
      });
      toast.success(`Evidence requested from ${evidenceParty}`);
      setEvidenceNote('');
      await refreshAll();
    } catch (err) {
      toast.error(err.message || 'Request failed');
    } finally {
      setActing(false);
    }
  };

  const handleResolve = async (status) => {
    if (!selectedClaimId || !faultType) {
      toast.warn('Select a fault type before resolving');
      return;
    }
    if (faultType === 'seller_partial' && (refundPct < 40 || refundPct > 60)) {
      toast.warn('Partial refund must be between 40% and 60%');
      return;
    }
    const confirmMsg =
      status === 'refund_processed'
        ? `Execute outcome for ${faultType}? This triggers refunds and escrow actions.`
        : status === 'rejected'
        ? 'Reject this claim and release seller payout?'
        : 'Record decision without executing payments yet?';
    if (!window.confirm(confirmMsg)) return;

    try {
      setActing(true);
      await disputeAdminService.resolve(selectedClaimId, {
        status,
        faultType,
        refundPct: faultType === 'seller_partial' ? Number(refundPct) : undefined,
        adminNotes: adminNotes.trim(),
        overrideFault: true,
      });
      toast.success(status === 'decision_made' ? 'Decision recorded' : 'Dispute resolved');
      await refreshAll();
      if (status !== 'decision_made') {
        setSelectedClaimId(null);
        setDetail(null);
      }
    } catch (err) {
      toast.error(err.message || 'Resolution failed');
    } finally {
      setActing(false);
    }
  };

  const handleResolveAppeal = async (appealId, status) => {
    const notes = window.prompt('Outcome notes for appellant:', '');
    if (notes === null) return;
    let revisedRefundPct;
    let revisedFaultType;
    if (status === 'partial') {
      revisedRefundPct = Number(window.prompt('Revised refund % (40–60):', '50'));
    }
    if (status === 'upheld') {
      revisedFaultType = window.prompt('Revised fault type (optional):', '') || undefined;
    }
    try {
      setActing(true);
      await disputeAdminService.resolveAppeal(appealId, {
        status,
        outcomeNotes: notes,
        revisedRefundPct,
        revisedFaultType,
      });
      toast.success(`Appeal ${status}`);
      await refreshAll();
    } catch (err) {
      toast.error(err.message || 'Appeal resolution failed');
    } finally {
      setActing(false);
    }
  };

  const chain = detail?.photoChain || detail?.photoChainSummary;
  const sla = detail?.sla;
  const highCount = disputes.filter((d) => d.severity === 'HIGH').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FaBalanceScale className="text-indigo-600" />
            Dispute arbitration
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            3-photo chain · SLA tracking · fault assignment · outcome execution
          </p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          disabled={loading || detailLoading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
        >
          <FaSync className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">Queue ({bucket})</p>
          <p className="text-2xl font-bold text-slate-900">{disputes.length}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">HIGH severity</p>
          <p className="text-2xl font-bold text-red-600">{highCount}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">Awaiting delivery photo</p>
          <p className="text-2xl font-bold text-amber-600">
            {disputes.filter((d) => d.photoGracePending).length}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-slate-500">System suggestion pending</p>
          <p className="text-2xl font-bold text-indigo-600">
            {disputes.filter((d) => d.suggestedFaultType && !d.faultType).length}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setView('disputes')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            view === 'disputes' ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-600'
          }`}
        >
          Disputes
        </button>
        <button
          type="button"
          onClick={() => {
            setView('appeals');
            setSelectedClaimId(null);
            setDetail(null);
          }}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            view === 'appeals' ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-600'
          }`}
        >
          Senior ops appeals
        </button>
      </div>

      {view === 'disputes' && (
      <div className="flex gap-2 flex-wrap">
        {['active', 'escalated', 'resolved'].map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => {
              setBucket(b);
              setSelectedClaimId(null);
              setDetail(null);
            }}
            className={`rounded-lg px-4 py-2 text-sm font-semibold capitalize ${
              bucket === b
                ? 'bg-indigo-600 text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {b === 'escalated' ? 'Senior ops escalated' : b}
          </button>
        ))}
      </div>
      )}

      {view === 'appeals' ? (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-sm">
            Open appeals (5 working day SLA)
          </div>
          {loading ? (
            <p className="p-6 text-sm text-slate-500">Loading…</p>
          ) : appeals.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">No open appeals.</p>
          ) : (
            <ul className="divide-y">
              {appeals.map((a) => (
                <li key={a.appealId} className="px-4 py-4">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <p className="font-mono text-xs text-indigo-700">{a.appealId}</p>
                      <p className="text-sm font-semibold">
                        {a.claimId} · {a.orderId}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">
                        {a.appellantType} appeal · due {formatDt(a.resolutionDeadline)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={acting}
                        onClick={() => handleResolveAppeal(a.appealId, 'upheld')}
                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Uphold
                      </button>
                      <button
                        type="button"
                        disabled={acting}
                        onClick={() => handleResolveAppeal(a.appealId, 'partial')}
                        className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Partial
                      </button>
                      <button
                        type="button"
                        disabled={acting}
                        onClick={() => handleResolveAppeal(a.appealId, 'rejected')}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{a.grounds}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-2 rounded-xl border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-sm">Claims</div>
          {loading ? (
            <p className="p-6 text-sm text-slate-500">Loading…</p>
          ) : disputes.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">No {bucket} disputes.</p>
          ) : (
            <ul className="divide-y max-h-[640px] overflow-y-auto">
              {disputes.map((d) => (
                <li key={d.claimId}>
                  <button
                    type="button"
                    onClick={() => handleSelect(d.claimId)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${
                      selectedClaimId === d.claimId ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-mono text-xs text-indigo-700">{d.claimId}</p>
                        <p className="text-sm font-semibold text-slate-900">{d.partName || d.orderId}</p>
                        <p className="text-xs text-slate-500">{d.issueLabel}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          d.severity === 'HIGH'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {d.severity || 'STANDARD'}
                      </span>
                    </div>
                    {d.suggestedFaultType && (
                      <p className="mt-1 text-xs text-indigo-600">
                        Suggested: {d.suggestedFaultType.replace(/_/g, ' ')}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="xl:col-span-3 space-y-4">
          {!selectedClaimId ? (
            <div className="rounded-xl border bg-white p-10 text-center text-slate-500 text-sm">
              Select a claim to review the 3-photo chain and resolve.
            </div>
          ) : detailLoading ? (
            <div className="rounded-xl border bg-white p-10 text-center text-slate-500 text-sm">
              Loading dispute…
            </div>
          ) : detail ? (
            <>
              <div className="rounded-xl border bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-sm text-indigo-700">{detail.claimId}</p>
                    <h3 className="text-lg font-bold text-slate-900">{detail.partName}</h3>
                    <p className="text-sm text-slate-500">
                      {detail.orderId} · {detail.issueLabel}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold">
                    {detail.internalStatus || detail.status}
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</p>
                {detail.arbitrationReason && (
                  <p className="mt-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
                    Auto arbitration: {detail.arbitrationReason}
                  </p>
                )}
              </div>

              {sla && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <SlaBadge
                    label={`First response (${sla.firstResponseHours}h)`}
                    deadline={sla.firstResponseDeadline}
                    overdue={sla.firstResponseOverdue}
                    urgent={detail.severity === 'HIGH'}
                  />
                  <SlaBadge
                    label="Resolution target (72h)"
                    deadline={sla.resolutionDeadline}
                    overdue={sla.resolutionOverdue}
                  />
                  <SlaBadge
                    label="Escrow hold max (10d)"
                    deadline={sla.escrowHoldMax}
                    overdue={sla.escrowEscalationDue}
                    urgent={sla.escrowEscalationDue}
                  />
                </div>
              )}

              <div className="rounded-xl border bg-white p-4">
                <h4 className="text-sm font-bold text-slate-900 mb-1">3-photo arbitration chain</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Compare listing (locked at publish) · dispatch (pre-handoff) · delivery (buyer claim)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <PhotoTile
                    title="Listing"
                    url={chain?.listingPhotoUrl}
                    tone="listing"
                  />
                  <PhotoTile
                    title="Dispatch"
                    url={chain?.dispatchPhotoUrl}
                    comparisonResult={chain?.dispatchComparisonResult}
                    comparisonScore={chain?.dispatchComparisonScore}
                    tone="dispatch"
                  />
                  <PhotoTile
                    title="Delivery"
                    url={chain?.deliveryPhotoUrl || detail.photos?.[0]?.url}
                    comparisonResult={chain?.deliveryComparisonResult}
                    comparisonScore={chain?.deliveryComparisonScore}
                    tone="delivery"
                  />
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Listing ≠ dispatch (HIGH) → seller fault · Dispatch ≠ delivery (HIGH) → carrier
                  fault
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-white p-4 text-sm">
                  <h4 className="font-bold text-slate-900 mb-2">Buyer history</h4>
                  <p>Claim rate: {detail.buyerClaimRate ?? '—'}%</p>
                  <p>Total disputes: {detail.buyerDisputeCount ?? '—'}</p>
                  <p>Fraud flags: {detail.buyerFraudFlagCount ?? 0}</p>
                </div>
                <div className="rounded-xl border bg-white p-4 text-sm">
                  <h4 className="font-bold text-slate-900 mb-2">Seller strikes</h4>
                  {detail.sellerStrikeSummary ? (
                    <>
                      <p>Cancellation: {detail.sellerStrikeSummary.activeCancellationStrikes ?? 0}</p>
                      <p>SLA: {detail.sellerStrikeSummary.activeSlaStrikes ?? 0}</p>
                      <p>Dispute: {detail.sellerStrikeSummary.activeDisputeStrikes ?? '—'}</p>
                      <p>Listings paused: {detail.sellerStrikeSummary.listingsPaused ? 'Yes' : 'No'}</p>
                    </>
                  ) : (
                    <p className="text-slate-500">No strike data</p>
                  )}
                </div>
              </div>

              {detail.orderTimeline?.length > 0 && (
                <div className="rounded-xl border bg-white p-4">
                  <h4 className="text-sm font-bold text-slate-900 mb-2">Order timeline</h4>
                  <ul className="space-y-1 text-xs text-slate-600 max-h-32 overflow-y-auto">
                    {detail.orderTimeline.map((ev, idx) => (
                      <li key={idx} className="flex gap-2">
                        <FaClock className="shrink-0 mt-0.5 text-slate-400" />
                        <span>
                          <strong>{ev.state}</strong> — {formatDt(ev.timestamp)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {bucket === 'active' && (
                <>
                  <div className="rounded-xl border bg-white p-4 space-y-3">
                    <h4 className="text-sm font-bold text-slate-900">Request additional evidence</h4>
                    <p className="text-xs text-slate-500">
                      Once per party · 48h response window · no adverse inference from silence
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {['buyer', 'seller'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          disabled={detail.evidenceRequests?.[p] || acting}
                          onClick={() => setEvidenceParty(p)}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize ${
                            evidenceParty === p
                              ? 'bg-indigo-600 text-white'
                              : 'border border-slate-200 text-slate-600'
                          } ${detail.evidenceRequests?.[p] ? 'opacity-40' : ''}`}
                        >
                          {p}
                          {detail.evidenceRequests?.[p] ? ' ✓ sent' : ''}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={evidenceNote}
                      onChange={(e) => setEvidenceNote(e.target.value)}
                      rows={2}
                      placeholder="What evidence do you need?"
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      disabled={acting || detail.evidenceRequests?.[evidenceParty]}
                      onClick={handleRequestEvidence}
                      className="rounded-lg bg-slate-800 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40"
                    >
                      Request from {evidenceParty}
                    </button>
                  </div>

                  <div className="rounded-xl border bg-white p-4 space-y-4">
                    <h4 className="text-sm font-bold text-slate-900">Fault assignment & resolution</h4>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase">Fault type</label>
                      <select
                        value={faultType}
                        onChange={(e) => setFaultType(e.target.value)}
                        className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                      >
                        <option value="">Select fault type…</option>
                        {FAULT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                            {detail.suggestedFaultType === o.value ? ' (suggested)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {faultType === 'seller_partial' && (
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase">
                          Buyer refund % (40–60)
                        </label>
                        <input
                          type="number"
                          min={40}
                          max={60}
                          value={refundPct}
                          onChange={(e) => setRefundPct(Number(e.target.value))}
                          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase">Admin notes</label>
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        rows={2}
                        className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={acting || !faultType}
                        onClick={() => handleResolve('decision_made')}
                        className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-800 disabled:opacity-40"
                      >
                        <FaCheck /> Record decision
                      </button>
                      <button
                        type="button"
                        disabled={acting || !faultType}
                        onClick={() => handleResolve('refund_processed')}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                      >
                        <FaCheck /> Execute outcome
                      </button>
                      <button
                        type="button"
                        disabled={acting}
                        onClick={() => handleResolve('rejected')}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                      >
                        <FaTimes /> Reject claim
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
      )}
    </div>
  );
}
